import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuestionEditorContext } from '../../context/create-question.context';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertTextarea from 'components/HubertUI/HubertTextArea';
import MutedText from 'components/common/MutedText';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { addOptions } from 'rtk/Question.Editor.Slice';
import {
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionMode,
  selectQuestionOptions,
} from 'rtk/questionEditorSelector';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';
import { useUser } from 'hooks/useUser';
import { isHubertAdminUser } from 'types/user.types';

/**
 * Parses a pasted options list, one option per line. A line containing a tab (Excel/Sheets'
 * two-column copy format) splits into label + reference; otherwise the whole line is the label
 * and reference is left undefined (falls back to label wherever it's consumed downstream).
 *
 * Drops duplicate labels (exact match after trimming) — both duplicates within the pasted text
 * itself (first occurrence wins) and any label already present in `existingLabels`, so pasting
 * the same list twice by mistake doesn't create doubled-up options.
 */
export const parsePastedOptions = (
  text: string,
  existingLabels: ReadonlySet<string> = new Set(),
): { label: string; reference?: string }[] => {
  const seen = new Set(existingLabels);
  const result: { label: string; reference?: string }[] = [];

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const [label, reference] = line.split('\t').map((part) => part.trim());
    if (!label || seen.has(label)) continue;

    seen.add(label);
    result.push(reference ? { label, reference } : { label });
  }

  return result;
};

const PasteOptionsList = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const existingOptions = useAppSelector((state) => selectQuestionOptions(state, questionId));
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);
  const user = useUser();
  const disabled = isReadOnly;

  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  if (inTemplateEditAndUneditable) return null;

  // Tab is how a manually-typed line sets a reference value (the two-column format), so it must
  // insert a literal tab here instead of doing its usual job of moving focus to the next field.
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    e.preventDefault();
    const el = e.currentTarget;
    const { selectionStart, selectionEnd } = el;
    setText(text.slice(0, selectionStart) + '\t' + text.slice(selectionEnd));
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = selectionStart + 1;
    });
  };

  const existingLabels = new Set(
    existingOptions.map((option) => option.label[questionLanguage]?.trim()).filter((label): label is string => !!label),
  );
  const parsed = parsePastedOptions(text, existingLabels);

  const handleConfirm = () => {
    if (!parsed.length) return;
    dispatch(addOptions({ questionId, language: questionLanguage, options: parsed }));
    setText('');
    setIsOpen(false);
  };

  const handleCancel = () => {
    setText('');
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <div className={clsx('p-1')}>
        <HubertButton type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setIsOpen(true)}>
          {t('selects.pasteOptionsButton')}
        </HubertButton>
      </div>
    );
  }

  return (
    <div className={clsx('flex flex-col gap-2 p-1')}>
      <HubertTextarea
        ref={textareaRef}
        autoFocus
        disabled={disabled}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleTextareaKeyDown}
        placeholder={t('selects.pasteOptionsPlaceholder')}
        rows={6}
      />
      {isHubertAdminUser(user) ? <MutedText text={t('selects.pasteOptionsHint')} /> : null}
      <div className={clsx('flex flex-row gap-2')}>
        <HubertButton type="button" size="sm" disabled={disabled || !parsed.length} onClick={handleConfirm}>
          {t('selects.pasteOptionsConfirm', { count: parsed.length })}
        </HubertButton>
        <HubertButton type="button" variant="ghost" size="sm" disabled={disabled} onClick={handleCancel}>
          {t('selects.pasteOptionsCancel')}
        </HubertButton>
      </div>
    </div>
  );
};

export default PasteOptionsList;
